// One plain sentence per block of איכות קריאה — what the numbers say, so the
// reader doesn't have to work it out from a table. Pure functions over the
// GET /stats/extraction payload (ExtractionQualityView.jsx renders them).
// Every one returns null when there isn't enough data to say anything true.

const nf = new Intl.NumberFormat('he-IL');
const fmt = (n) => nf.format(n || 0);
const pctInt = (n, total) => (total ? Math.round((n / total) * 100) : 0);

export function formatSeconds(s) {
  if (s == null) return null;
  if (s < 60) return `${s} שנ׳`;
  const m = Math.floor(s / 60), r = s % 60;
  return `${m}:${String(r).padStart(2, '0')} דק׳`;
}

// 49,474 → "49.5K": a cost ledger is read at the thousand, not the digit.
export function formatTokens(n) {
  if (n == null) return '—';
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return fmt(n);
}

// Suppliers with too few judged decisions say nothing reliable; a median
// needs timed decisions, a facet comparison needs more (one slow photo is
// not "images are slow").
const MIN_DIFFED = 5;
export const MIN_TIMED = 10;
export const MIN_FACET_TIMED = 30;

// Rounding never lies at the edges: 199/200 is 99%, not 100%, and 1/300 is
// "<1%", not 0% — "100% unchanged" with corrections in the table is the kind
// of thing a reader remembers.
export function sharePct(n, of) {
  const p = Math.round((n / of) * 100);
  if (p === 100 && n < of) return '99%';
  if (p === 0 && n > 0) return '<1%';
  return `${p}%`;
}
export const TARGET_CUT_PCT = 40;

// The one tinted callout on the tab: where the selected range's KPI (reviewer
// seconds per decided draft) sits against the baseline's and the target, in
// SECONDS (a "110% slower" over three decisions is noise dressed as a
// verdict). Silent until both sides carry the server's minimum sample.
export function baselineInsight(baseline, kpi) {
  if (!baseline.kpi?.comparable || !kpi?.comparable) return null;
  if (baseline.kpi.seconds_per_decision == null || kpi.seconds_per_decision == null || baseline.target_seconds == null) return null;
  const cur = kpi.seconds_per_decision, base = baseline.kpi.seconds_per_decision, target = baseline.target_seconds;
  if (cur <= target) return `היעד הושג: ${formatSeconds(cur)} מול יעד של ${formatSeconds(target)} (קו בסיס ${formatSeconds(base)}).`;
  const toTarget = cur - target;
  if (cur < base) return `${formatSeconds(base - cur)} מהר יותר מקו הבסיס · עוד ${formatSeconds(toTarget)} ליעד של ${formatSeconds(target)}.`;
  if (cur === base) return `זהה לקו הבסיס · עוד ${formatSeconds(toTarget)} ליעד של ${formatSeconds(target)}.`;
  return `${formatSeconds(cur - base)} איטי יותר מקו הבסיס (${formatSeconds(base)}) · היעד ${formatSeconds(target)}.`;
}

export function supplierInsight(suppliers) {
  const judged = suppliers.filter(s => !s.no_supplier && s.diffed >= MIN_DIFFED);
  if (!judged.length) return null;
  const mostCorrected = [...judged].sort((a, b) => b.corrections - a.corrections || a.unchanged_pct - b.unchanged_pct)[0];
  const timed = judged.filter(s => s.median_seconds != null && s.timed >= MIN_TIMED);
  const slowest = timed.length ? [...timed].sort((a, b) => b.median_seconds - a.median_seconds)[0] : null;
  const parts = [`הכי הרבה תיקונים: ${mostCorrected.name} (${fmt(mostCorrected.corrections)} מתוך ${fmt(mostCorrected.diffed)})`];
  if (slowest) parts.push(`הכי איטי לסקירה: ${slowest.name} (${formatSeconds(slowest.median_seconds)})`);
  return `${parts.join(' · ')} — כאן כללי קריאה לספק ישתלמו קודם.`;
}

export function fieldsInsight(fields, labels) {
  if (!fields.diffed) return null;
  const top = [...fields.rows].sort((a, b) => b.count - a.count)[0];
  if (!top || !top.count) return 'אף שדה לא תוקן — הקריאה עברה כמו שהיא.';
  return `השדה שמתוקן הכי הרבה: ${labels[top.key] || top.key} (${fmt(top.count)} מתוך ${fmt(fields.diffed)} עם קריאה).`;
}

export function deletionsInsight(d, reasonLabels, sourceLabels) {
  if (!d.total) return null;
  const top = (obj) => Object.entries(obj).sort((a, b) => b[1] - a[1])[0];
  const [reason, n] = top(d.by_reason);
  const [source, m] = top(d.by_source);
  const via = source === 'unknown'
    ? `דרך ההעלאה לא ידועה ב-${fmt(m)} (מסמכים שכבר נוקו סופית)`
    : `${fmt(m)} מ${sourceLabels[source] || source}`;
  let text = `${fmt(d.total)} מחיקות · ${reasonLabels[reason] || reason} ${fmt(n)} · ${via}.`;
  if (reason === 'duplicate' && n / d.total >= 0.5 && d.total >= MIN_DIFFED) text += ' זיהוי כפילויות בזמן ההעלאה היה חוסך את רובן.';
  return text;
}

export function usageInsight(u) {
  if (!u.rows.length) return null;
  const month = u.months[0];
  const rows = u.rows.filter(r => r.month === month);
  const calls = rows.reduce((n, r) => n + r.calls, 0);
  const tokens = rows.reduce((n, r) => n + r.input_tokens + r.output_tokens, 0);
  const claimed = rows.reduce((n, r) => n + r.claimed, 0);
  const failed = rows.reduce((n, r) => n + (r.parse_failed || 0), 0);
  const [y, m] = month.split('-');
  const parts = [`${m}/${y}: ${fmt(calls)} קריאות`, `${formatTokens(Math.round(tokens / Math.max(calls, 1)))} טוקנים לקריאה`, `${pctInt(claimed, calls)}% נשמרו`];
  if (failed) parts.push(`${pctInt(failed, calls)}% נכשלו`);
  return `${parts.join(' · ')}.`;
}

export function shapeInsight(shape) {
  if (!shape.reviewed) return null;
  const g = Object.fromEntries(shape.groups.map(x => [x.key, x]));
  const cmp = (a, b, labelA, labelB) => {
    if (!g[a] || !g[b] || g[a].median_seconds == null || g[b].median_seconds == null) return null;
    if ((g[a].timed || 0) < MIN_FACET_TIMED || (g[b].timed || 0) < MIN_FACET_TIMED) return null;
    const d = g[a].median_seconds - g[b].median_seconds;
    if (Math.abs(d) < 5) return `${labelA} ו${labelB} נסקרים באותו זמן`;
    return d > 0
      ? `${labelA} לוקח ${formatSeconds(d)} יותר מ${labelB}`
      : `${labelA} מהיר ב-${formatSeconds(-d)} מ${labelB}`;
  };
  const parts = [cmp('pdf', 'image', 'PDF', 'תמונה'), cmp('rules', 'no_rules', 'מסמך עם כללי קריאה', 'מסמך בלי'), cmp('multi_page', 'one_page', 'מסמך רב-עמודים', 'עמוד אחד')].filter(Boolean);
  return parts.length ? `${parts.join(' · ')}.` : null;
}
